import React, { useRef, useState } from 'react';

interface TiltCardProps {
  src: string;
  alt?: string;
  className?: string;
}

export function TiltCard({ src, alt = 'Profile picture', className = '' }: TiltCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState('');
  const [glare, setGlare] = useState({ x: 50, y: 50, opacity: 0 });
  const [shadow, setShadow] = useState('0 25px 50px -12px rgba(0,0,0,0.25)');

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    const rotateX = ((y - cy) / cy) * -12;
    const rotateY = ((x - cx) / cx) * 12;

    const glareX = (x / rect.width) * 100;
    const glareY = (y / rect.height) * 100;

    const shadowX = ((x - cx) / cx) * -20;
    const shadowY = ((y - cy) / cy) * -20;

    setTransform(`perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.04, 1.04, 1.04)`);
    setGlare({ x: glareX, y: glareY, opacity: 0.18 });
    setShadow(`${shadowX}px ${shadowY + 30}px 60px -10px rgba(79,70,229,0.35), ${shadowX * 0.5}px ${shadowY * 0.5 + 15}px 30px -5px rgba(0,0,0,0.2)`);
  }

  function handleMouseLeave() {
    setTransform('perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)');
    setGlare(g => ({ ...g, opacity: 0 }));
    setShadow('0 25px 50px -12px rgba(0,0,0,0.25)');
  }

  return (
    <div
      ref={cardRef}
      className={`relative rounded-2xl overflow-hidden cursor-pointer select-none ${className}`}
      style={{
        transform,
        boxShadow: shadow,
        transition: 'transform 0.08s ease-out, box-shadow 0.08s ease-out',
        willChange: 'transform',
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover block"
        draggable={false}
      />

      {/* Glare overlay */}
      <div
        className="absolute inset-0 pointer-events-none rounded-2xl"
        style={{
          background: `radial-gradient(circle at ${glare.x}% ${glare.y}%, rgba(255,255,255,${glare.opacity}) 0%, transparent 65%)`,
          transition: 'opacity 0.08s ease-out',
        }}
      />

      {/* Subtle border shine */}
      <div
        className="absolute inset-0 pointer-events-none rounded-2xl"
        style={{
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
        }}
      />
    </div>
  );
}
